/**
 * Clipboard for legacy Agentic Flow canvas (single-node copy / paste across sessions).
 * Distinct from V2 studio clipboard keys.
 */

export const LEGACY_FLOW_NODE_CLIP_SESSION_KEY = 'chatbot:agentic:legacy-flow-node-clipboard';

export const LEGACY_FLOW_NODE_CLIP_PREFIX = 'CHATBOT_AGENTIC_LEGACY_NODE:';

export function wrapLegacyNodeEnvelope(node) {
  if (!node || typeof node !== 'object') return null;
  return {
    clipVersion: 1,
    kind: 'agentic-legacy-flow-node',
    node: JSON.parse(JSON.stringify(node)),
    meta: { copiedAt: Date.now() },
  };
}

export function parseLegacyNodeEnvelope(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.clipVersion !== 1 || raw.kind !== 'agentic-legacy-flow-node') return null;
  if (!raw.node || typeof raw.node !== 'object') return null;
  return raw.node;
}

export function writeLegacyNodeClipboard(envelope) {
  if (!envelope) return;
  try {
    sessionStorage.setItem(LEGACY_FLOW_NODE_CLIP_SESSION_KEY, JSON.stringify(envelope));
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[legacy flow] sessionStorage clipboard write failed', e);
    }
  }
}

export function readLegacyNodeClipboard() {
  try {
    const t = sessionStorage.getItem(LEGACY_FLOW_NODE_CLIP_SESSION_KEY);
    if (!t) return null;
    return parseLegacyNodeEnvelope(JSON.parse(t));
  } catch {
    return null;
  }
}

export function hasLegacyNodeClipboard() {
  return Boolean(readLegacyNodeClipboard());
}

export async function writeLegacyNodeSystemClipboard(envelope) {
  if (!envelope || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
  const line = LEGACY_FLOW_NODE_CLIP_PREFIX + JSON.stringify(envelope);
  await navigator.clipboard.writeText(line);
}

export async function tryReadLegacyNodeSystemClipboard() {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) return null;
  const t = await navigator.clipboard.readText();
  if (typeof t !== 'string' || !t.startsWith(LEGACY_FLOW_NODE_CLIP_PREFIX)) return null;
  try {
    const env = JSON.parse(t.slice(LEGACY_FLOW_NODE_CLIP_PREFIX.length));
    return parseLegacyNodeEnvelope(env);
  } catch {
    return null;
  }
}

/**
 * Clone for paste: new id, offset position. Avoid duplicate start/end nodes.
 * @param {function(string): object} [getDefaultConfig] — e.g. FlowDesignTab `getDefaultNodeConfig` when remapping type
 */
export function cloneLegacyNodeForPaste(template, existingNodes = [], getDefaultConfig) {
  const types = new Set((existingNodes || []).map((n) => n?.type));
  const base = JSON.parse(JSON.stringify(template));
  const origType = base.type;
  let nextType = origType;
  if (origType === 'start' && types.has('start')) {
    nextType = 'step';
  }
  if (origType === 'end' && types.has('end')) {
    nextType = 'step';
  }
  const id = `${nextType}_${Date.now()}`;
  base.id = id;
  base.type = nextType;
  base.position = {
    x: (Number(base.position?.x) || 200) + 48,
    y: (Number(base.position?.y) || 200) + 48,
  };
  base.data = base.data && typeof base.data === 'object' ? { ...base.data } : { label: id, type: nextType, config: {} };
  base.data.type = nextType;
  if (nextType !== origType) {
    const prevLabel = base.data.label || base.label || origType;
    base.data.label = `${prevLabel} (copy)`;
    if (typeof getDefaultConfig === 'function') {
      base.data.config = { ...getDefaultConfig(nextType) };
    } else {
      base.data.config = {};
    }
  } else {
    base.data.label = base.data.label || base.label || id;
  }
  base.label = base.data.label;
  return base;
}
